import { Input, Modal } from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import { toDoc31HeaderReason } from '../../utils/doc31';
import style from './reason-prompt.module.scss';

const MIN_REASON = 3;
const MAX_REASON = 500;

export interface Doc31ReasonRequest {
    /** The action, word for word as on its button: "Go live", "Pause play". */
    actionLabel: string;
    /** What will happen, one short sentence. */
    description?: string;
    danger?: boolean;
}

export interface Doc31ReasonPrompt {
    /** Resolves with the reason, or null when the operator cancels. */
    prompt: (request: Doc31ReasonRequest) => Promise<string | null>;
    /** Render once, anywhere in the caller's tree. */
    element: React.ReactElement;
}

/**
 * Lifecycle moves and coupon withdrawals record why they were made
 * (`X-Command-Reason`); History shows it. The confirm button repeats the
 * action's own name.
 */
export function useDoc31ReasonPrompt(): Doc31ReasonPrompt {
    const [request, setRequest] = useState<Doc31ReasonRequest | null>(null);
    const [reason, setReason] = useState('');
    const resolver = useRef<((value: string | null) => void) | null>(null);

    const prompt = useCallback((next: Doc31ReasonRequest) => {
        resolver.current?.(null);
        setReason('');
        setRequest(next);
        // Executor form on purpose: the build targets Safari 14 and Chrome 87
        // (vite.config.mts), and `Promise.withResolvers` needs Safari 17.4.
        return new Promise<string | null>((resolve) => {
            resolver.current = resolve;
        });
    }, []);

    const close = (value: string | null) => {
        resolver.current?.(value);
        resolver.current = null;
        setRequest(null);
    };

    const { reason: cleaned, unsupported } = toDoc31HeaderReason(reason);
    const problem =
        unsupported.length > 0
            ? `Remove ${unsupported.join(' ')}: History can't store it.`
            : cleaned.length > MAX_REASON
            ? `Keep it under ${MAX_REASON} characters.`
            : null;
    const element = (
        <Modal
            open={request !== null}
            title={request?.actionLabel}
            okText={request?.actionLabel}
            okButtonProps={{
                danger: request?.danger,
                disabled: cleaned.length < MIN_REASON || problem !== null,
            }}
            cancelText="Cancel"
            onOk={() => close(cleaned)}
            onCancel={() => close(null)}
            destroyOnClose
        >
            {request?.description && (
                <p className={style.description}>{request.description}</p>
            )}
            <label className={style.label} htmlFor="doc31-reason">
                Why?
            </label>
            <Input.TextArea
                id="doc31-reason"
                autoFocus
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Saved in History with your name."
                maxLength={MAX_REASON}
                showCount
                autoSize={{ minRows: 3, maxRows: 6 }}
            />
            {problem && (
                <p className={style.problem} role="alert">
                    {problem}
                </p>
            )}
        </Modal>
    );

    return { prompt, element };
}
